import * as React from 'react';
import { useQuery } from "graphql-hooks";
import { Row, Col, Button, Form, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import { useEffect, useState } from 'react';
import Reaptcha from 'reaptcha';
import { useAPIErrorContext } from './../../contexts/apiErrorContext'
import Helper from '../../utilities/helper'
import { PermissionsGate } from '../../utilities/PermissionsGate';
import { ACCESS_VALUES, PageNames } from '../../utilities/constValues';
import { PermissionsRequired } from '../Shared/PermissionsRequired';
import { useLocation } from 'react-router-dom';
import { Confirmation } from './Confirmation'
import { Hero } from '../Shared/Hero';
import { useFetchWrapper } from '../../hooks/useFetchWrapper';
import { NavigationCardSet } from '../Shared/NavigationCards/NavigationCardSet';
import { ErrorPage } from '../Shared/ErrorPage';
import { ErrorBanner } from '../Shared/ErrorBanner';
import { MetaTags } from '../Shared/MetaTags';
import { Loading } from '../Shared/Loading';
import { clientConfig } from '../../utilities/configService';
import { FormContainer } from '../Shared/Forms/FormContainer';
import { FormHeading } from '../Shared/Forms/FormHeading';

const CMS_QUERY = `query datoQuery {
    pageData: pageContact {
        fieldHeroTitle
        fieldBodyContent1
        fieldBodyContent2
        sectionHelpLine {
            fieldLabel
            fieldPhone
            fieldEmailAddress
        }
        fieldCardNavigationSectionTitle
        fieldNavigationCards {
            id
            fieldCardTitle
            fieldCardLink
            fieldCardContent
            fieldCardImage {
                alt
                url
                title
            }
        }
        seo: _seoMetaTags {
            attributes
            content
            tag
        }
    }
}`;

interface IOrganizationType {
    organizationTypeID: string,
    organizationTypeName: string
}

interface IJobRoleType {
    jobRoleTypeID: string,
    jobRoleTypeName: string
}

interface IReferralOption {
    contactHearAboutTypeID: number,
    contactHearAboutName: string

}

export const ContactUs = () => {
    const fetchWrapper = useFetchWrapper();
    const { addError } = useAPIErrorContext();
    const { loading, error, data } = useQuery(CMS_QUERY);
    const [captchaVerified, setCaptchaVerified] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [organizationTypeOptions, setOrganizationTypeOptions] = useState([]);
    const [jobRoleTypeOptions, setJobRoleTypeOptions] = useState([]);
    const [roleSelect, setroleSelect] = useState("0");
    const [organizationTypeSelect, setOrganizationTypeSelect] = useState("0");
    const [referralSelect, setReferralSelect] = useState(0);
    const [referralOther, setReferralOther] = useState("");
    const [referralOptions, setReferralOptions] = useState<IReferralOption[]>([]);
    const [showReferralOtherInput, setShowReferralOtherInput] = useState(false);

    const location = useLocation();
    useEffect(() => {
        Helper.setPagePath(location.pathname);
    }, [location]);

    // eslint-disable-next-line
    useEffect(() => {
        populateOrganizationType();
        populateJobRoleType();
        populateReferralOptions();
    }, []);

    const populateOrganizationType = () => {
        fetchWrapper.get('organization/gettypes')
            .then((data) => {
                if (data.success && data.organizationTypes) {
                    setOrganizationTypeOptions(data.organizationTypes);
                }
            });
    };

    const populateReferralOptions = () => {
        fetchWrapper.get('contact/getreferraloptions')
            .then((data) => {
                if (data.success && data.referralTypes) {
                    setReferralOptions(data.referralTypes);
                }
            });
    };

    const populateJobRoleType = () => {
        fetchWrapper.get('jobrole')
            .then((data) => {
                if (data.success && data.jobRoles) {
                    setJobRoleTypeOptions(data.jobRoles);
                }
            });
    };

    const [formErrors, setFormErrors] = useState({
        firstName: '',
        lastName: '',
        email: '',
        organizationType: '',
        role: ''
    });

    const [contactForm, setContactForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        organizationName: '',
        message: ''
    });

    function handleFirstNameChange(e: { target: { value: any; }; }) {
        const updatedForm = {
            ...contactForm,
            firstName: e.target.value
        };
        setContactForm(updatedForm);
        if (formErrors.firstName !== '') {
            validateForm(updatedForm);
        }
    }

    function handleLastNameChange(e: { target: { value: any; }; }) {
        const updatedForm = {
            ...contactForm,
            lastName: e.target.value
        };
        setContactForm(updatedForm);
        if (formErrors.lastName !== '') {
            validateForm(updatedForm);
        }
    }

    function handleEmailChange(e: { target: { value: any; }; }) {
        const updatedForm = {
            ...contactForm,
            email: e.target.value
        };
        setContactForm(updatedForm);
        if (formErrors.email !== '') {
            validateForm(updatedForm);
        }
    }

    function handleOrganizationNameChange(e: { target: { value: any; }; }) {
        const updatedForm = {
            ...contactForm,
            organizationName: e.target.value
        };
        setContactForm(updatedForm);
        if (formErrors.email !== '') {
            validateForm(updatedForm);
        }
    }

    function handleMessageChange(e: { target: { value: any; }; }) {
        setContactForm({
            ...contactForm,
            message: e.target.value
        });
    }

    const handleRoleChange = (e: { target: { value: any; }; }) => {
        setroleSelect(e.target.value);
        if (formErrors.role !== '') {
            validateForm(contactForm, e.target.value);
        }
    };

    const handleOrganizationTypeChange = (e: { target: { value: any; }; }) => {
        setOrganizationTypeSelect(e.target.value);
        if (formErrors.organizationType !== '') {
            validateForm(contactForm, roleSelect, e.target.value);
        }
    };

    const handleReferralTypeChange = (e: { target: { value: any; }; }) => {
        var value = e.target.value;
        setReferralSelect(value);
        var referralOpt = referralOptions.find((item) => item.contactHearAboutTypeID.toString() === value.toString());
        if (referralOpt?.contactHearAboutName.toLowerCase() === "other") {
            setShowReferralOtherInput(true);
        }
        else {
            setShowReferralOtherInput(false);
        }
    };

    const emailValidationPattern = new RegExp(/^[a-zA-Z0-9.!#$%&'*+/=?^_{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/);
    const containsOnlySpacesValidationPattern = "^\\s+$";

    //Validate Form
    const errorClass = (error: string | any[]) => {
        return (error.length === 0 ? '' : 'is-invalid');
    }

    // Change handlers pass in the values they just set, since state updates are not visible until the next render.
    const validateForm = (form = contactForm, role = roleSelect, organizationType = organizationTypeSelect) => {
        var fieldValidationError = '';
        var fieldErrors = {
            firstName: '',
            lastName: '',
            email: '',
            organizationType: '',
            role: ''
        }

        if (form.firstName == null ||
            form.firstName.length <= 0 ||
            form.firstName.match(containsOnlySpacesValidationPattern)) {
            fieldValidationError = 'First Name is required.';
            fieldErrors.firstName = 'First Name is required.'
        }

        if (form.lastName == null ||
            form.lastName.length <= 0 ||
            form.lastName.match(containsOnlySpacesValidationPattern)) {
            fieldValidationError = 'Last Name is required.';
            fieldErrors.lastName = 'Last Name is required.'
        }

        if (form.email.length <= 0) {
            fieldValidationError = 'Email is required.';
            fieldErrors.email = 'Email is required.'
        }
        else if (!form.email.match(emailValidationPattern)) {
            fieldValidationError = 'Email is invalid.';
            fieldErrors.email = 'Email is invalid.'
        }

        if (organizationType === '0') {
            fieldValidationError = 'Organization Type is required.';
            fieldErrors.organizationType = 'Organization Type is required.'
        }

        if (role === '0') {
            fieldValidationError = 'Job role is required.';
            fieldErrors.role = 'Job role is required.'
        }

        // Always show the latest result, so fixing the last remaining error clears it too.
        setFormErrors(fieldErrors);

        if (fieldValidationError.length > 0) {
            console.log("formErrors state: ", formErrors);
            return false;
        }
        else {
            return true;
        }
    }

    const onVerify = async (recaptchaResponse: any) => {
        if (recaptchaResponse) {
            var recaptchaverificationrequest = { token: recaptchaResponse };

            fetchWrapper.post('recaptchaverification', 'application/json', recaptchaverificationrequest)
                .then((data) => {
                    if (data.success) {
                        setCaptchaVerified(true);
                    }
                });
        }
    };

    const validateAndPostForm = async (e: any) => {

        e.preventDefault();

        let formIsValid = validateForm();
        const applicationName = clientConfig?.APPLICATION_NAME || '';

        if (formIsValid) {
            var contactUsRequest = {
                firstName: contactForm.firstName,
                lastName: contactForm.lastName,
                organizationName: contactForm.organizationName,
                email: contactForm.email,
                message: contactForm.message,
                organizationRole: roleSelect,
                organizationTypeID: organizationTypeSelect,
                applicationName: applicationName,
                referralTypeID: referralSelect,
                referralOther: (showReferralOtherInput ? referralOther : null)
            };

            fetchWrapper.post('contact', 'application/json', contactUsRequest)
                .then((data) => {
                    if (data.success) {
                        setFormSubmitted(true);
                    }
                    else {
                        setFormSubmitted(false);
                        addError("Error submitting Contact Us form", data.message);
                    }
                }).catch((error: any) => {
                    setFormSubmitted(false);
                    addError("Error submitting Contact Us form", error);
                });
        }
    };

    const formatPhoneNumber = (phoneNumberString: string) => {
        var cleaned = ('' + phoneNumberString).replace(/\D/g, '');
        var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            var intlCode = (match[1] ? '+1 ' : '');
            return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
        }
        return null;
    }

    if (!error && !loading) {
        return (
            <PermissionsGate requestedPermission={ACCESS_VALUES.Resources} renderError={<PermissionsRequired showParticipantMsg={true} />} pageName={PageNames.ContactUs}>
            <div>
                {formSubmitted ? (
                    <Confirmation />
                ) : (
                    <div className="contactRequest">
                        <Hero pageTitle={data.pageData.fieldHeroTitle} />
                        <ErrorBanner key="contactUsPage" errorCode="form" />
                        <FormContainer title={data.pageData.fieldBodyContent1} description={data.pageData.fieldBodyContent2}>
                            <Col sm={8} className="formBody">
                                <FormHeading title="Send us an email:" textAlign="alignLeft" light={false} />
                                <div className="formInnerBody">
                                    <Row className="justify-content-end">
                                        <Col sm={8} className="formRequiredContainer">
                                            <span className="formRequired"></span> = required field
                                        </Col>
                                    </Row>
                                    <Form>
                                        <FormGroup row className={`form-group ${errorClass(formErrors.firstName)}`}>
                                            <Label for="firstName" sm={4} className="formRequired ">First Name</Label>
                                            <Col sm={8}>
                                                <Input className="fieldWidth215" type="text" name="firstname" id="firstName" placeholder="" required maxLength={150} value={contactForm.firstName} onChange={handleFirstNameChange} invalid={formErrors.firstName !== ''} pattern={containsOnlySpacesValidationPattern.toString()} />
                                                <FormFeedback>{formErrors.firstName}</FormFeedback>
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row className={`form-group ${errorClass(formErrors.lastName)}`}>
                                            <Label for="lastName" sm={4} className="formRequired">Last Name</Label>
                                            <Col sm={8}>
                                                <Input className="fieldWidth215" type="text" name="lastname" id="lastName" placeholder="" required maxLength={150} value={contactForm.lastName} onChange={handleLastNameChange} invalid={formErrors.lastName !== ''} pattern={containsOnlySpacesValidationPattern.toString()} />
                                                <FormFeedback>{formErrors.lastName}</FormFeedback>

                                            </Col>
                                        </FormGroup>
                                        <FormGroup row className={`form-group ${errorClass(formErrors.role)}`}>
                                            <Label for="roleSelect" sm={4} className="formRequired">Job Role</Label>
                                            <Col sm={8}>
                                                <Input className="fieldWidth215" type="select" name="select" id="roleSelect" value={roleSelect} required onChange={handleRoleChange} invalid={formErrors.role !== ''}>
                                                    <option key="0" value="0" disabled>
                                                        Select an option
                                                    </option>
                                                    {jobRoleTypeOptions.map((option: IJobRoleType) => {
                                                        return (
                                                            <option key={option.jobRoleTypeName} value={option.jobRoleTypeName}>
                                                                {option.jobRoleTypeName}
                                                            </option>)
                                                    })};
                                                </Input>
                                                <FormFeedback>{formErrors.role}</FormFeedback>
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row >
                                            <Label for="organizationName" sm={4} className="notRequired">Organization Name</Label>
                                            <Col sm={8}>
                                                <Input className="fieldWidth215" type="text" name="organizationName" id="organizationName" placeholder="" maxLength={150} value={contactForm.organizationName} onChange={handleOrganizationNameChange} />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row className={`form-group ${errorClass(formErrors.organizationType)}`}>
                                            <Label for="organizationType" sm={4} className="formRequired">Organization Type</Label>
                                            <Col sm={8}>
                                                <Input className="fieldWidth215" type="select" name="select" id="organizationType" value={organizationTypeSelect} required onChange={handleOrganizationTypeChange} invalid={formErrors.organizationType !== ''}>
                                                    <option key="0" value="0" disabled>
                                                        Select an option
                                                    </option>
                                                    {organizationTypeOptions.map((option: IOrganizationType) => {
                                                        return (
                                                            <option key={option.organizationTypeID} value={option.organizationTypeID}>
                                                                {option.organizationTypeName}
                                                            </option>)
                                                    })};
                                                </Input>
                                                <FormFeedback>{formErrors.organizationType}</FormFeedback>
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row className={`form-group ${errorClass(formErrors.email)}`}>
                                            <Label for="emailAddress" sm={4} className="formRequired">Email Address</Label>
                                            <Col sm={8}>
                                                <Input className="fieldWidth215" type="email" name="email" id="emailAddress" placeholder="" required value={contactForm.email} onChange={handleEmailChange} invalid={formErrors.email !== ''}
                                                    pattern={emailValidationPattern.toString()} />
                                                <FormFeedback>{formErrors.email}</FormFeedback>
                                            </Col>
                                        </FormGroup>

                                        <FormGroup row className={`form-group`}>
                                            <Label for="contactHearAboutDD" sm={4} className="">How did you hear about our program?</Label>
                                            <Col sm={8}>
                                                <Input className="fieldWidth300" type="select" name="select" id="contactHearAboutDD" value={referralSelect}
                                                    onChange={(e) => handleReferralTypeChange(e)}>
                                                    <option key="0" value="0" disabled>
                                                        Select an option
                                                    </option>
                                                    {referralOptions.map((option: IReferralOption) => {
                                                        return (
                                                            <option key={option.contactHearAboutTypeID} value={option.contactHearAboutTypeID}>
                                                                {option.contactHearAboutName}
                                                            </option>)
                                                    })};
                                                </Input>
                                            </Col>
                                            </FormGroup>
                                            {showReferralOtherInput ? (
                                                <FormGroup row className={`form-group`}>
                                                    <Label for="referralOther" sm={4} className="">Please specify:</Label>
                                                    <Col sm={8}>
                                                        <Input className="fieldWidth300" type="text" name="referralOther" id="referralOther" placeholder="" value={referralOther}
                                                            onChange={(e) => setReferralOther(e.target.value)} maxLength={200} />
                                                    </Col>
                                                </FormGroup>
                                            ) : ('')}
                                        <FormGroup row>
                                            <Label for="message" sm={4} className="notRequired">Message</Label>
                                            <Col sm={8}>
                                                <Input className="fieldWidth300" type="textarea" name="text" id="message" maxLength={2000} value={contactForm.message} onChange={handleMessageChange} />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Col sm={4}>&nbsp;</Col>
                                            <Col sm={8}>
                                                <div className="recaptchaSection">
                                                    <Reaptcha sitekey={import.meta.env.REACT_APP_CAPTCHA_SITE_KEY ? import.meta.env.REACT_APP_CAPTCHA_SITE_KEY : ''} onVerify={onVerify} />
                                                </div>
                                            </Col>
                                        </FormGroup>
                                        <hr />
                                        <div className="buttonRow">
                                            <Button className="submitButton solidNeg secondaryButton iconRightArrow" onClick={validateAndPostForm} disabled={!captchaVerified}>Submit</Button>
                                        </div>
                                    </Form>
                                </div>
                            </Col>
                            <Col sm={4} className="formDisplayColumn">
                                <FormHeading title="Help lines" textAlign="alignLeft" light={true} />
                                <div className="contactHelpSection">
                                    {data.pageData.sectionHelpLine.map((item: any, index: any) =>
                                        <div className="contactHelpItem" key={index}>
                                            {item.fieldLabel ? <p>{item.fieldLabel}</p> : ''}
                                            {item.fieldPhone ? <p>{formatPhoneNumber(item.fieldPhone)}</p> : ''}
                                            {item.fieldEmailAddress ? <p>{item.fieldEmailAddress}</p> : ''}
                                        </div>
                                    )}
                                </div>
                            </Col>
                        </FormContainer>
                        {/* include "More About Program" tri-cards */}
                        <NavigationCardSet PageData={data.pageData} />
                    </div>
                )}

                {/* set meta tags */}
                <MetaTags seoData={data.pageData.seo} />

            </div>
            </PermissionsGate>
        )
    }
    else if (!error) {
        return (<Loading showContent={true} includeHero={true} />);
    }
    else {
        return (<ErrorPage showContactForm={false} includeHero={true} />);
    }


};
